import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AppId, provideBaseHref, provideSharedWorker } from '@ecam/eventq-components';

import { routes } from './app.routes';
import { EVENTQ_OKTA_PROVIDERS } from './core/auth/okta.providers';
import { apiOriginInterceptor } from './core/interceptors/api-origin.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { initializeMockPageDefaults } from './core/mocks/initialize-mock-page-defaults';
import { provideAgentShortcuts } from './core/shortcuts/agent-shortcuts';
import { ACCESS_TOKEN_STORAGE_KEY, AuthSessionService } from './core/services/auth-session.service';
import { BrowserHistoryLockService } from './core/services/browser-history-lock.service';
import { CROSS_APP_BRIDGE } from './core/services/cross-app-bridge';
import { CrossAppWorkspaceSyncService } from './core/services/cross-app-workspace-sync.service';
import { MockCrossAppBridge } from './core/services/mock-cross-app-bridge';
import { EventParkedSyncService } from './core/services/event-parked-sync.service';
import { LiveStreamRuntimeConfigService } from './core/services/live-stream-runtime-config.service';
import { EventWorkspacePrefetchService } from './core/services/event-workspace-prefetch.service';
import { PageStateInitService } from './core/services/page-state-init.service';
import { PatrolSessionSyncService } from './core/services/patrol-session-sync.service';
import { provideAgentSignalR } from './core/services/provide-agent-signalr';
import { environment } from '../environments/environment';
import { claimSiblingAppWindowName, EVENTQ_SYNC_WINDOW_NAME } from './core/utils/sibling-app-href';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideRouter(routes),
    provideBaseHref(),
    provideHttpClient(withFetch(), withInterceptors([apiOriginInterceptor, authInterceptor])),
    provideAgentShortcuts(),
    EVENTQ_OKTA_PROVIDERS,
    { provide: CROSS_APP_BRIDGE, useExisting: MockCrossAppBridge },
    provideAppInitializer(() => {
      const auth = inject(AuthSessionService);
      auth.restoreFromStorage();
      window.addEventListener('storage', (event) => {
        if (event.key !== ACCESS_TOKEN_STORAGE_KEY) {
          return;
        }
        if (!event.newValue && auth.isAuthenticated()) {
          auth.applyRemoteLogout();
        } else if (event.newValue) {
          void auth.applyRemoteSession();
        }
      });
      inject(BrowserHistoryLockService);
      inject(EventParkedSyncService);
      inject(PatrolSessionSyncService);
      inject(PageStateInitService);
      inject(EventWorkspacePrefetchService);
      inject(CrossAppWorkspaceSyncService);
      claimSiblingAppWindowName(EVENTQ_SYNC_WINDOW_NAME);
    }),
    provideAppInitializer(initializeMockPageDefaults()),
    provideAppInitializer(() => inject(LiveStreamRuntimeConfigService).load()),
    provideSharedWorker({
      appId: AppId.EVENTQ,
      workerUrl: environment.sharedWorkerUrl,
    }),
    provideAgentSignalR(),
  ],
};
